//
// Common utility functions
//
import { notification } from "antd";
import { AxiosError } from "axios";
import {} from "antd/es/upload/interface";
import { parse as parseContentDisposition } from 'content-disposition';
/**
 * Utility function to process a axios error
 *
 * @param axiosError the axios error object
 * @param errorDisplayFunc a function that takes a single string error message parameters
 */
export function axiosErrorHandler(axiosError, errorDisplayFunc) {
    if (!errorDisplayFunc) {
        errorDisplayFunc = showErrorNotification;
    }
    if (axiosError.response) {
        // Unauthorised responses are ignored here.  They are dealt with via an interceptor in AuthenticationManager.js
        if (axiosError.response.status != 401 && axiosError.response.data) {
            const errorResponse = axiosError.response.data;
            if (errorResponse.code && errorResponse.code === "CONCURRENT_MODIFICATION") {
                errorDisplayFunc("Your changes could not be saved and have been discarded. This is because another user has changed the same details.  Please review the changes and reapply your modification.");
            }
            else if (errorResponse.messages) {
                errorDisplayFunc(errorResponse.messages.join(" and "));
            }
            else if (axiosError.response.status == 402) { // Payment Required
                errorDisplayFunc("A credit card is required however this profile does not have a saved credit card.");
            }
            else {
                errorDisplayFunc("Unknown error occurred (status: " + axiosError.response.status + "). Please try again.  If the problem persists report this error to SubTrux technical support.");
            }
        }
        else if (axiosError.response.status != 401) {
            errorDisplayFunc("Unknown network error");
        }
    }
    else if (axiosError.request) {
        errorDisplayFunc("Timeout waiting for server response.");
    }
    else {
        errorDisplayFunc(axiosError.message);
    }
}
export const sanitiseFileName = (filename) => {
    return filename.replace(/[^a-zA-Z0-9\\._ ]/g, '');
};
export function showErrorNotification(errorMessage) {
    notification.error({
        title: 'Error',
        description: errorMessage,
        duration: 15
    });
}
export function showWarningNotification(errorMessage) {
    notification.warning({
        title: 'Warning',
        description: errorMessage,
        duration: 15
    });
}
export function showSuccessNotification(description) {
    notification.success({
        title: 'Success',
        description,
        duration: 10
    });
}
export function displayImageUploadErrorMessage(file) {
    let errorMessage = "Unknown error uploading image";
    if (file.response && file.response.messages) {
        errorMessage = file.response.messages.join(" and ");
    }
    else if (file.error && file.error.status === 413) { // Status 413 - Request Entity Too Large
        errorMessage = "File is too large to upload";
    }
    showErrorNotification(errorMessage);
}
export function checkErrorStatusAndCode(axiosError, expectedStatus, expectedErrorCode) {
    return axiosError.response &&
        axiosError.response.status === expectedStatus &&
        axiosError.response.data &&
        axiosError.response.data.code &&
        axiosError.response.data.code === expectedErrorCode;
}
export function isConcurrentModificationError(axiosError) {
    return checkErrorStatusAndCode(axiosError, 412, "CONCURRENT_MODIFICATION");
}
export function isDuplicateError(axiosError) {
    return checkErrorStatusAndCode(axiosError, 412, "DUPLICATE");
}
export function isErrorOverridable(axiosError) {
    return checkErrorStatusAndCode(axiosError, 412, "OVERRIDABLE");
}
export function isPaymentRequiredError(axiosError) {
    return axiosError.response && axiosError.response.status === 402;
}
export function isNull(x) {
    return x === null;
}
export function isNotNull(x) {
    return !isNull(x);
}
export function isUndefined(x) {
    return typeof x === 'undefined';
}
export function isNotUndefined(x) {
    return !isUndefined(x);
}
export function isDefinedAndNotNull(x) {
    return isNotUndefined(x) && isNotNull(x);
}
export function isNullOrBlank(str) {
    if (typeof str === "string") {
        return !(str && str.trim().length);
    }
    return !str;
}
export function isNullOrUndefined(input) {
    return input === null || typeof input === 'undefined';
}
export function isNullOrUndefinedOrEmpty(input) {
    if (isNullOrUndefined(input)) {
        return true;
    }
    if (typeof input === "string") {
        return input.trim().length === 0;
    }
    if (Array.isArray(input)) {
        return input.length === 0;
    }
    return false;
}
export function firstNonNull(...items) {
    for (let item of items) {
        if (isDefinedAndNotNull(item)) {
            return item;
        }
    }
}
/**
 * Tagged template function for excluding undefined or null values.
 */
export const nonEmpty = (strings, ...expr) => {
    let result = '';
    strings.forEach((string, i) => {
        result += string + (expr[i] ?? '');
    });
    return result;
};
/**
 * generalization of the code from: https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore
 */
export function groupBy(xs, f) {
    return xs.reduce((r, v, i, a, k = f(v)) => ((r[k] || (r[k] = [])).push(v), r), {});
}
export function formatSeconds(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [
        h,
        m > 9 ? m : '0' + m,
        s > 9 ? s : '0' + s,
    ].filter(s => s).join(':');
}
export function formatMetres(metres, rounded) {
    if (!metres) {
        return '';
    }
    if (metres < 1000) {
        return metres + ' m';
    }
    const hectometres = Math.round(metres / 100);
    const kilometres = rounded ? Math.round(hectometres / 10) : hectometres / 10;
    return kilometres + ' km';
}
/**
 * null friendly string compare
 */
export function strcmp(s1, s2) {
    const v1 = s1 || "";
    const v2 = s2 || "";
    return v1 < v2 ? -1 : +(v1 > v2);
}
// null friendly object compare
export function objcmp(o1, o2) {
    if (o1 && !o2) {
        return 1;
    }
    if (!o1 && o2) {
        return -1;
    }
    return 0;
}
export const debounce = (fn, ms = 300) => {
    let timeoutId;
    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), ms);
    };
};
export function lowerCaseIncludes(testVal, includeVal) {
    return testVal && testVal.toLowerCase().includes(includeVal);
}
export function getSessionStorageItemWithDefault(key, defaultVal, isObj) {
    const storedItem = sessionStorage.getItem(key);
    if (storedItem) {
        if (!isObj && typeof defaultVal === "number") {
            return parseInt(storedItem);
        }
        return isObj ? JSON.parse(storedItem) : storedItem;
    }
    return defaultVal;
}
export function setOrRemoveSessionStorageItem(key, value) {
    if (isNullOrUndefined(value)) {
        sessionStorage.removeItem(key);
    }
    else {
        switch (typeof value) {
            case "object":
                sessionStorage.setItem(key, JSON.stringify(value));
                break;
            case "number":
            case "boolean":
                sessionStorage.setItem(key, String(value));
                break;
            case "string":
                sessionStorage.setItem(key, value);
                break;
            default:
                throw Error("Unsupported type: " + typeof value);
        }
    }
}
export const parseContentDispositionHeader = (header) => {
    if (!header) {
        return null;
    }
    return parseContentDisposition(header);
};
export function removeEmojis(text) {
    if (text) {
        return text.replace(/([\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2694-\u2697]|\uD83E[\uDD10-\uDD5D])/g, '');
    }
    return text;
}
//
// https://github.com/kennethjiang/js-file-download/blob/master/file-download.js
//
export function downloadAsFile(data, filename, mime) {
    const blob = new Blob([data], { type: mime || 'application/octet-stream' });
    const sanitisedFileName = sanitiseFileName(filename);
    // @ts-ignore TODO
    if (typeof window.navigator.msSaveBlob !== 'undefined') {
        // IE workaround for "HTML7007: One or more blob URLs were
        // revoked by closing the blob for which they were created.
        // These URLs will no longer resolve as the data backing
        // the URL has been freed."
        // @ts-ignore TODO
        window.navigator.msSaveBlob(blob, sanitisedFileName);
    }
    else {
        const blobURL = window.URL.createObjectURL(blob);
        let tempLink = document.createElement('a');
        tempLink.style.display = 'none';
        tempLink.href = blobURL;
        tempLink.setAttribute('download', sanitisedFileName);
        // Safari thinks _blank anchor are pop ups. We only want to set _blank
        // target if the browser does not support the HTML5 download attribute.
        // This allows you to download files in desktop safari if pop up blocking
        // is enabled.
        if (typeof tempLink.download === 'undefined') {
            tempLink.setAttribute('target', '_blank');
        }
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
        window.URL.revokeObjectURL(blobURL);
    }
}
export function isNumber(text) {
    if (text === null || typeof text === 'undefined') {
        return false;
    }
    if (typeof text === 'number') {
        return true;
    }
    let parsedValue = parseFloat(text);
    if (isNaN(parsedValue) || !isFinite(parsedValue)) {
        return false;
    }
    return true;
}
export function roundToPrecision(value, precision = 0) {
    let power = 10 ** precision;
    return Math.round(value * power) / power;
}
export function downloadUsingIFrame(url) {
    // @ts-suppress TODO
    let iframe = document.getElementById("hiddenDownloader");
    if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = "hiddenDownloader";
        iframe.style.visibility = 'hidden';
        document.body.appendChild(iframe);
    }
    // @ts-ignore TODO
    iframe.src = url;
}
export function conditionFilter(obj) {
    if (obj.hasOwnProperty("condition")) {
        return obj.condition;
    }
    return true;
}
export function calcNetTonnes(gross, tare) {
    return (Math.round(gross * 1000) - Math.round(tare * 1000)) / 1000;
}
